import { useState, useEffect } from 'react';
import type { Product } from '@shared/types/product.types';

interface UseProductsOptions {
  category?: string;
  featured?: boolean;
}

export function useProducts(options?: UseProductsOptions) {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (options?.category) params.set('category', options.category);
    if (options?.featured) params.set('featured', 'true');

    const url = `/api/products${params.toString() ? `?${params}` : ''}`;

    setIsLoading(true);
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch products (${res.status})`);
        return res.json();
      })
      .then((json) => {
        setProducts(json.data || []);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setIsLoading(false);
      });
  }, [options?.category, options?.featured]);

  return { products, isLoading, error };
}

export function useProduct(slug: string) {
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    fetch(`/api/products/${slug}`)
      .then((res) => {
        if (!res.ok) throw new Error('Product not found');
        return res.json();
      })
      .then((json) => {
        setProduct(json.data);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setIsLoading(false);
      });
  }, [slug]);

  return { product, isLoading, error };
}
